/**
 * Automation Rule Service
 * 
 * This service evaluates automation rules against timesheets and executes
 * the appropriate actions based on rule conditions.
 */

import type { Timesheet, Employee } from '@/types/timesheet';
import type { StageAutomationRule, RuleCondition, RuleConditionGroup, RuleExecutionLog, RuleEvaluationResult, RuleConditionField, RuleOperator } from '@/types/automation-rules';

export class AutomationRuleService {
  /**
   * Evaluate all rules for a specific stage against a timesheet
   */
  static async evaluateStageRules(
    timesheet: Timesheet,
    employee: Employee,
    stageId: string,
    rules: StageAutomationRule[],
    historicalTimesheets: Timesheet[]
  ): Promise<RuleEvaluationResult> {
    // Sort rules by priority (1 = highest)
    const sortedRules = [...rules]
      .filter(r => r.isEnabled)
      .sort((a, b) => a.priority - b.priority);

    const matchedRules: StageAutomationRule[] = [];
    const executionLogs: RuleExecutionLog[] = [];
    let shouldAutoApprove = false;
    let shouldAutoAdvance = false;
    let shouldRequireReview = false;
    const notesToAdd: string[] = [];
    const notificationsToSend = { userIds: [] as string[], roles: [] as string[] };
    const flags: { label: string; color: string }[] = [];

    for (const rule of sortedRules) {
      const evaluationResult = this.evaluateRule(
        rule,
        timesheet,
        employee,
        historicalTimesheets
      );

      // Create execution log
      const log: RuleExecutionLog = {
        id: `log-${Date.now()}-${Math.random()}`,
        ruleId: rule.id,
        ruleName: rule.name,
        ruleVersion: 1, // TODO: Implement versioning
        timesheetId: timesheet.id,
        stageId: stageId,
        executedAt: new Date(),
        matched: evaluationResult.matched,
        conditionResults: evaluationResult.conditionResults,
        actionsExecuted: [],
        executedBy: 'system'
      };

      if (evaluationResult.matched) {
        matchedRules.push(rule);

        // Execute actions
        for (const action of rule.actions) {
          try {
            switch (action.type) {
              case 'auto-approve':
                shouldAutoApprove = true;
                log.actionsExecuted.push({
                  action: 'auto-approve',
                  success: true,
                  timestamp: new Date()
                });
                break;

              case 'auto-advance':
                shouldAutoAdvance = true;
                log.actionsExecuted.push({
                  action: 'auto-advance',
                  success: true,
                  timestamp: new Date()
                });
                break;

              case 'require-review':
                shouldRequireReview = true;
                log.actionsExecuted.push({
                  action: 'require-review',
                  success: true,
                  timestamp: new Date()
                });
                break;

              case 'add-note':
                if (action.parameters.note) {
                  notesToAdd.push(action.parameters.note);
                  log.actionsExecuted.push({
                    action: 'add-note',
                    success: true,
                    timestamp: new Date()
                  });
                }
                break;

              case 'notify':
                if (action.parameters.notifyUserIds) {
                  notificationsToSend.userIds.push(...action.parameters.notifyUserIds);
                }
                if (action.parameters.notifyRoles) {
                  notificationsToSend.roles.push(...action.parameters.notifyRoles);
                }
                log.actionsExecuted.push({
                  action: 'notify',
                  success: true,
                  timestamp: new Date()
                });
                break;

              case 'flag':
                if (action.parameters.flagLabel) {
                  flags.push({
                    label: action.parameters.flagLabel,
                    color: action.parameters.flagColor || 'blue'
                  });
                  log.actionsExecuted.push({
                    action: 'flag',
                    success: true,
                    timestamp: new Date()
                  });
                }
                break;
            }
          } catch (error) {
            log.actionsExecuted.push({
              action: action.type,
              success: false,
              error: error instanceof Error ? error.message : 'Unknown error',
              timestamp: new Date()
            });
          }
        }

        // Stop if rule says so
        if (rule.stopOnMatch) {
          executionLogs.push(log);
          break;
        }
      }

      executionLogs.push(log);
    }

    return {
      matchedRules,
      executionLogs,
      shouldAutoApprove,
      shouldAutoAdvance,
      shouldRequireReview,
      notesToAdd,
      notificationsToSend,
      flags
    };
  }

  /**
   * Evaluate a single rule's conditions
   */
  private static evaluateRule(
    rule: StageAutomationRule,
    timesheet: Timesheet,
    employee: Employee,
    historicalTimesheets: Timesheet[]
  ): {
    matched: boolean;
    conditionResults: { condition: string; result: boolean; actualValue?: any; expectedValue?: any }[];
  } {
    const conditionResults: { condition: string; result: boolean; actualValue?: any; expectedValue?: any }[] = [];
    const matched = this.evaluateConditionGroup(
      rule.conditionGroup,
      timesheet,
      employee,
      historicalTimesheets,
      conditionResults
    );

    return { matched, conditionResults };
  }

  /**
   * Recursively evaluate condition groups
   */
  private static evaluateConditionGroup(
    group: RuleConditionGroup,
    timesheet: Timesheet,
    employee: Employee,
    historicalTimesheets: Timesheet[],
    conditionResults: { condition: string; result: boolean; actualValue?: any; expectedValue?: any }[]
  ): boolean {
    const results: boolean[] = [];

    // Evaluate direct conditions
    for (const condition of group.conditions) {
      const result = this.evaluateCondition(condition, timesheet, employee, historicalTimesheets);
      results.push(result.matched);
      conditionResults.push({
        condition: condition.displayText || `${condition.field} ${condition.operator} ${condition.value}`,
        result: result.matched,
        actualValue: result.actualValue,
        expectedValue: condition.value
      });
    }

    // Evaluate nested groups
    if (group.groups && group.groups.length > 0) {
      for (const nestedGroup of group.groups) {
        const nestedResult = this.evaluateConditionGroup(
          nestedGroup,
          timesheet,
          employee,
          historicalTimesheets,
          conditionResults
        );
        results.push(nestedResult);
      }
    }

    // Apply operator
    if (results.length === 0) return true; // Empty group evaluates to true
    
    if (group.operator === 'AND') {
      return results.every(r => r);
    } else {
      return results.some(r => r);
    }
  }

  /**
   * Evaluate a single condition
   */
  private static evaluateCondition(
    condition: RuleCondition,
    timesheet: Timesheet,
    employee: Employee,
    historicalTimesheets: Timesheet[]
  ): { matched: boolean; actualValue: any } {
    const actualValue = this.getFieldValue(condition.field, timesheet, employee, historicalTimesheets);
    const matched = this.compareValues(actualValue, condition.operator, condition.value);
    return { matched, actualValue };
  }

  /**
   * Get the actual value of a field from timesheet/employee data
   */
  private static getFieldValue(
    field: RuleConditionField,
    timesheet: Timesheet,
    employee: Employee,
    historicalTimesheets: Timesheet[]
  ): any {
    // Employee fields
    if (field.startsWith('employee.')) {
      const employeeField = field.replace('employee.', '');
      switch (employeeField) {
        case 'id':
          return employee.id;
        case 'name':
          return employee.name;
        case 'department':
          return employee.department;
        case 'role':
          return employee.role;
        case 'managerId':
          return employee.managerId;
        case 'consecutiveCleanSubmissions':
          return this.calculateConsecutiveCleanSubmissions(employee.id, historicalTimesheets);
        case 'hasViolationsInLast30Days':
          return this.hasViolationsInPeriod(employee.id, historicalTimesheets, 30);
        case 'hasViolationsInLast60Days':
          return this.hasViolationsInPeriod(employee.id, historicalTimesheets, 60);
        case 'hasViolationsInLast90Days':
          return this.hasViolationsInPeriod(employee.id, historicalTimesheets, 90);
        case 'averageWeeklyHours':
          return this.calculateAverageWeeklyHours(employee.id, historicalTimesheets);
      }
    }

    // Timesheet fields
    if (field.startsWith('timesheet.')) {
      const timesheetField = field.replace('timesheet.', '');
      switch (timesheetField) {
        case 'totalHours':
          return this.calculateTotalHours(timesheet);
        case 'contractedHours':
          return employee.workingPattern?.hoursPerWeek || 40;
        case 'overtimeHours': {
          const total = this.calculateTotalHours(timesheet);
          const contracted = employee.workingPattern?.hoursPerWeek || 40;
          return Math.max(0, total - contracted);
        }
        case 'projectCount':
          return new Set(timesheet.entries.map(e => e.projectId)).size;
        case 'allProjectsApproved':
          // TODO: Implement project approval status check
          return true;
        case 'hasWeekendWork':
          return timesheet.entries.some(entry => 
            entry.hours.saturday > 0 || entry.hours.sunday > 0
          );
        case 'billableHours':
          // TODO: Calculate billable hours based on project billable status
          return this.calculateTotalHours(timesheet);
        case 'nonBillableHours':
          // TODO: Calculate non-billable hours
          return 0;
        case 'submittedOnWeekend': {
          if (!timesheet.submittedAt) return false;
          const day = timesheet.submittedAt.getDay();
          return day === 0 || day === 6; // Sunday = 0, Saturday = 6
        }
        case 'daysSinceSubmission': {
          if (!timesheet.submittedAt) return 0;
          return Math.floor((Date.now() - timesheet.submittedAt.getTime()) / (1000 * 60 * 60 * 24));
        }
      }
    }

    return undefined;
  }

  /**
   * Compare values using the specified operator
   */
  private static compareValues(actualValue: any, operator: RuleOperator, expectedValue: any): boolean {
    // Handle undefined/null values
    if (actualValue === undefined || actualValue === null) {
      return operator === 'notEquals' || operator === 'isFalse';
    }

    switch (operator) {
      case 'equals':
        return actualValue === expectedValue;
      case 'notEquals':
        return actualValue !== expectedValue;
      case 'greaterThan':
        return Number(actualValue) > Number(expectedValue);
      case 'lessThan':
        return Number(actualValue) < Number(expectedValue);
      case 'greaterThanOrEqual':
        return Number(actualValue) >= Number(expectedValue);
      case 'lessThanOrEqual':
        return Number(actualValue) <= Number(expectedValue);
      case 'contains':
        return String(actualValue).toLowerCase().includes(String(expectedValue).toLowerCase());
      case 'notContains':
        return !String(actualValue).toLowerCase().includes(String(expectedValue).toLowerCase());
      case 'in':
        return Array.isArray(expectedValue) && expectedValue.includes(actualValue);
      case 'notIn':
        return Array.isArray(expectedValue) && !expectedValue.includes(actualValue);
      case 'isTrue':
        return actualValue === true;
      case 'isFalse':
        return actualValue === false;
      default:
        return false;
    }
  }

  /**
   * Calculate total hours for a timesheet
   */
  private static calculateTotalHours(timesheet: Timesheet): number {
    return timesheet.entries.reduce((sum, entry) => {
      return sum + Object.values(entry.hours).reduce((h, v) => h + v, 0);
    }, 0);
  }

  /**
   * Calculate consecutive clean submissions for an employee
   */
  private static calculateConsecutiveCleanSubmissions(
    employeeId: string,
    historicalTimesheets: Timesheet[]
  ): number {
    const employeeTimesheets = historicalTimesheets
      .filter(ts => ts.employeeId === employeeId && ts.status === 'approved')
      .sort((a, b) => b.weekEnding.getTime() - a.weekEnding.getTime());

    let consecutive = 0;
    for (const ts of employeeTimesheets) {
      // Check if timesheet was approved without issues
      if (ts.status === 'approved' && !ts.rejectionReason) {
        consecutive++;
      } else {
        break;
      }
    }

    return consecutive;
  }

  /**
   * Check if employee has violations in the last N days
   */
  private static hasViolationsInPeriod(
    employeeId: string,
    historicalTimesheets: Timesheet[],
    days: number
  ): boolean {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);

    return historicalTimesheets.some(ts => 
      ts.employeeId === employeeId &&
      ts.weekEnding >= cutoffDate &&
      (ts.status === 'rejected' || ts.rejectionReason)
    );
  }

  /**
   * Calculate average weekly hours for an employee
   */
  private static calculateAverageWeeklyHours(
    employeeId: string,
    historicalTimesheets: Timesheet[]
  ): number {
    const employeeTimesheets = historicalTimesheets
      .filter(ts => ts.employeeId === employeeId && ts.status === 'approved')
      .slice(0, 12); // Last 12 weeks

    if (employeeTimesheets.length === 0) return 0;

    const totalHours = employeeTimesheets.reduce((sum, ts) => {
      return sum + this.calculateTotalHours(ts);
    }, 0);

    return totalHours / employeeTimesheets.length;
  }

  /**
   * Validate a rule's structure and conditions
   */
  static validateRule(rule: StageAutomationRule): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!rule.name || rule.name.trim() === '') {
      errors.push('Rule name is required');
    }

    if (rule.priority < 1 || rule.priority > 10) {
      errors.push('Priority must be between 1 and 10');
    }

    if (!rule.conditionGroup || rule.conditionGroup.conditions.length === 0) {
      errors.push('At least one condition is required');
    }

    if (!rule.actions || rule.actions.length === 0) {
      errors.push('At least one action is required');
    }

    // Validate conditions
    if (rule.conditionGroup) {
      this.validateConditionGroup(rule.conditionGroup, errors);
    }

    // Validate actions
    rule.actions.forEach((action, index) => {
      if (action.type === 'add-note' && !action.parameters.note) {
        errors.push(`Action ${index + 1}: Note text is required`);
      }
      if (action.type === 'notify' && 
          (!action.parameters.notifyUserIds || action.parameters.notifyUserIds.length === 0) &&
          (!action.parameters.notifyRoles || action.parameters.notifyRoles.length === 0)) {
        errors.push(`Action ${index + 1}: At least one notification recipient is required`);
      }
      if (action.type === 'flag' && !action.parameters.flagLabel) {
        errors.push(`Action ${index + 1}: Flag label is required`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate a condition group recursively
   */
  private static validateConditionGroup(group: RuleConditionGroup, errors: string[]): void {
    group.conditions.forEach((condition, index) => {
      if (!condition.field) {
        errors.push(`Condition ${index + 1}: Field is required`);
      }
      if (!condition.operator) {
        errors.push(`Condition ${index + 1}: Operator is required`);
      }
      if (condition.value === undefined || condition.value === null || condition.value === '') {
        errors.push(`Condition ${index + 1}: Value is required`);
      }
    });

    if (group.groups) {
      group.groups.forEach(nestedGroup => {
        this.validateConditionGroup(nestedGroup, errors);
      });
    }
  }
}
